import React, { useCallback, useEffect, useState } from 'react';
import * as Styles from './Security.styles';
import {
  Skeleton,
  IconAtencion,
} from 'gatorcomponents';
import { RuleActionTypes } from './Security.types';
import { useResize } from '@/hooks/useResize';
import { RuleItem } from './components/RuleItem';
import { CreateRule } from './components/CreateRule';

const Security = ({
  locales,
  submitForm,
  toggleRuleStatus,
  submitBlockedUntil,
  showDeleteRuleConfirmModal,
  setFormData,
  rulesList,
  ruleToEdit,
  rulesListLoading,
  isSubmitLoading,
  rulesLeft,
  ruleFormData,
  getRulesListStatus,
  resetGetRulesStatus,
  actionOnSubmit,
}) => {
  const [ruleForm, setRuleForm] = useState({
    description: '',
    lockType: '',
    value: '',
  });

  const windowRect = useResize();
  const { width } = windowRect;
  const mobile = width < 960;
  const getListError = getRulesListStatus === 'failure';

  const rulesOptions = [
    {
      value: RuleActionTypes.COUNTRY,
      label: locales.traficBlock.ruleTypes[RuleActionTypes.COUNTRY].label,
    },
    {
      value: RuleActionTypes.CONTINENT,
      label: locales.traficBlock.ruleTypes[RuleActionTypes.CONTINENT].label,
    },
    {
      value: RuleActionTypes.SOURCE_IP_ADDRESS,
      label: locales.traficBlock.ruleTypes[RuleActionTypes.SOURCE_IP_ADDRESS].label,
    },
    {
      value: RuleActionTypes.URL,
      label: locales.traficBlock.ruleTypes[RuleActionTypes.URL].label,
    },
  ];

  const blockSubmit = !ruleForm.description || !ruleForm.value || isSubmitLoading;

  const getSelectedOptionLabel = (options, selectedValue) => {
    const selectedOption = options.find(option => option.value === selectedValue);
    return selectedOption ? selectedOption.label : '';
  };
  const getSelectedOptionValue = (options, selectedLabel) => {
    const selectedOption = options.find(option => option.label === selectedLabel);
    return selectedOption ? selectedOption.value : '';
  };

  const handleShowDeleteRuleConfirmModal = (id) => {
    showDeleteRuleConfirmModal && showDeleteRuleConfirmModal(id);
  };

  const handleToggleRuleStatus = (rule, e) => {
    e.preventDefault();
    const checked = !rule.enabled;
    toggleRuleStatus && toggleRuleStatus({ ...rule, enabled: checked });
  };

  const handleSetFormData = ({ ruleData, submitAction }) => {
    setFormData && setFormData({ ruleData, submitAction });
  };

  const getActionsList = rule => [
    {
      id: `${rule.id}-edit`,
      label: locales.dropdownActions.edit,
      onClick: () => handleSetFormData({ ruleData: rule, submitAction: 'edit' }),
    },
    {
      id: `${rule.id}-duplicate`,
      label: locales.dropdownActions.duplicate,
      onClick: () => handleSetFormData({ ruleData: rule, submitAction: 'create' }),
    },
    {
      id: `${rule.id}-delete`,
      label: locales.dropdownActions.delete,
      onClick: () => handleShowDeleteRuleConfirmModal(rule.rule_id),
    },
  ];

  const rules = rulesList;

  const { description, lockType, value } = ruleForm;

  const handleSubmitForm = () => {
    submitForm && submitForm({
      description,
      lockType,
      value,
    });
  };

  const handleFormChange = (name, value) => {
    if (name === 'lockType') {
      setRuleForm(prev => ({ ...prev, value: '' }));
    }
    setRuleForm(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = useCallback(() => {
    const ruleData = {
      description: '',
      lockType: '',
      value: '',
    };
    setRuleForm(ruleData);
  }, []);

  const changeFormInitialState = useCallback(() => {
    if (ruleFormData.value) {
      const { description, lockType, value } = ruleFormData;
      setRuleForm(prev => ({
        ...prev, description, lockType, value,
      }));
    }
  }, [ruleFormData]);

  const handleResetGetRulesStatus = useCallback(() => {
    resetGetRulesStatus && resetGetRulesStatus();
  }, [resetGetRulesStatus]);

  useEffect(() => {
    resetForm();
  }, [resetForm, rulesLeft, ruleFormData]);

  useEffect(() => {
    changeFormInitialState();
    handleResetGetRulesStatus();
  }, [changeFormInitialState, handleResetGetRulesStatus]);


  if (rulesListLoading) {
    return (
      <Styles.Wrapper>
        <Skeleton width="100%" height="300px" />
        <Skeleton width="100%" height="300px" />
      </Styles.Wrapper>
    );
  }

  const createRuleProps = () => ({
    locales,
    ruleForm,
    handleFormChange,
    rulesLeft,
    getSelectedOptionLabel,
    rulesOptions,
    getSelectedOptionValue,
    handleSubmitForm,
    isSubmitLoading,
    blockSubmit,
    submitBlockedUntil,
    ruleToEdit,
    actionOnSubmit,
  });

  return (
    <Styles.Wrapper data-testid="cloudflare-security">
      <CreateRule {...createRuleProps()} />

      {getListError && (
        <Styles.Container error>
          <IconAtencion size="xl" secondaryColor="problemLight" />

          <Styles.Title>
            {locales.getRulesError.title}
          </Styles.Title>

          <Styles.FirstRow>
            {locales.getRulesError.description}
          </Styles.FirstRow>
        </Styles.Container>
      )}

      {(!getListError && !!rules.length) && (
        <Styles.ContentWrapper>
          <Styles.Container>
            <Styles.Title>{locales.blockList.title}</Styles.Title>
            {!mobile && (
              <Styles.Table>
                <Styles.TableHeader>
                  <Styles.TableRow>
                    <Styles.TableHead>{locales.blockList.order}</Styles.TableHead>
                    <Styles.TableHead>{locales.blockList.name}</Styles.TableHead>
                    <Styles.TableHead>{locales.blockList.action}</Styles.TableHead>
                    <Styles.TableHead>{locales.blockList.toggleOn}</Styles.TableHead>
                  </Styles.TableRow>
                </Styles.TableHeader>

                {rules && rules.map((rule, index) => {
                  const dropdownList = getActionsList(rule);

                  return (
                    <RuleItem
                      rulesList={rules}
                      rule={rule}
                      ruleIndex={index}
                      dropdownList={dropdownList}
                      securityLocales={locales}
                      handleToggleRuleStatus={handleToggleRuleStatus}
                      isMobile={mobile}
                    />
                  );
                })}
              </Styles.Table>
            )}

            {mobile && (
              <Styles.MobileList>
                {rules && rules.map((rule, index) => {
                  const dropdownList = getActionsList(rule);

                  return (
                    <RuleItem
                      rulesList={rules}
                      rule={rule}
                      ruleIndex={index}
                      dropdownList={dropdownList}
                      securityLocales={locales}
                      toggleRuleStatus={handleToggleRuleStatus}
                      isMobile={mobile}
                    />
                  );
                })}
              </Styles.MobileList>
            )}
          </Styles.Container>
        </Styles.ContentWrapper>
      )}
    </Styles.Wrapper>
  );
};

export default Security;
